import React, { useState, useEffect, useRef } from 'react';
import { SalesLayout } from '@/components/SalesLayout';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useReactToPrint } from 'react-to-print';
import { Pagination } from '@/components/Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format date to DD-MM-YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

// Helper to get selected accessories from booking
const getSelectedAccessories = (booking) => {
  if (!booking?.accessories || typeof booking.accessories !== 'object') return [];
  return Object.entries(booking.accessories)
    .filter(([name, price]) => price && price > 0)
    .map(([name, price]) => ({ name, price }));
};

const reportTypes = [
  { id: 'booking_form', label: 'Booking Form', description: 'Vehicle booking details with customer information' },
  { id: 'delivery_note', label: 'Delivery Note', description: 'Delivery acknowledgment with vehicle handover details' },
  { id: 'pdi_report', label: 'PDI Report', description: 'Pre-Delivery Inspection checklist' },
  { id: 'warranty_letter', label: 'Warranty Acknowledgement', description: 'Warranty terms acknowledgment letter' },
];

const pdiCheckPoints = [
  'Charge the Battery',
  'Check Air Pressure',
  'Check Brakes',
  'Check Lights',
  'Check Horn',
  'Check Indicators',
  'Check Mirror',
  'Check Stand',
  'Check Seat Lock',
  'Check Key Lock',
  'Check Battery Lock',
  'GPS Installation',
  'Number Plate'
];

function Reports({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedReport, setSelectedReport] = useState('booking_form');
  const [searchQuery, setSearchQuery] = useState('');
  const [companyDetails, setCompanyDetails] = useState(null);
  const [pdiStatus, setPdiStatus] = useState(pdiCheckPoints.map(() => 'yes'));
  const [accessories, setAccessories] = useState({ allRoundGuard: 'no', seatCover: 'no' });
  const [shareEmail, setShareEmail] = useState('');
  const [shareWhatsApp, setShareWhatsApp] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareFormat, setShareFormat] = useState('pdf'); // 'pdf', 'excel', 'word'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const printRef = useRef();

  useEffect(() => {
    fetchBookings();
    fetchCompanyDetails();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings?limit=5000`);
      const sorted = response.data.sort((a, b) => {
        const aNum = parseInt(a.booking_no) || 0;
        const bNum = parseInt(b.booking_no) || 0;
        return bNum - aNum;
      });
      setBookings(sorted);
    } catch (error) {
      toast.error('Failed to fetch bookings');
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/masters?type=company`);
      if (response.data && response.data.length > 0) {
        setCompanyDetails(response.data[0]);
      }
    } catch (error) {
      console.log('No company details found');
    }
  };

  const filteredBookings = bookings.filter(b => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      b.booking_no?.toString().includes(query) ||
      b.customer_first_name?.toLowerCase().includes(query) ||
      b.chassis_no?.toLowerCase().includes(query)
    );
  });

  // Pagination
  const paginatedBookings = filteredBookings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${selectedReport}_${selectedBooking?.booking_no || 'report'}`,
    onAfterPrint: () => toast.success('Print completed!'),
    onPrintError: () => toast.error('Print failed!'),
  });

  const handleWhatsAppShare = () => {
    if (!shareWhatsApp || !selectedBooking) return;
    const b = selectedBooking;
    let msg = '';
    
    if (selectedReport === 'booking_form') {
      msg = `*Booking Details - ${companyDetails?.name || 'AutoBook'}*\n\nB.No: ${b.booking_no}\nCustomer: ${b.customer_first_name} ${b.customer_last_name}\nMobile: ${b.mobile_no_1}\nVehicle: ${b.brand_name} ${b.model}\nColor: ${b.color || '-'}\nChassis: ${b.chassis_no || '-'}\nReg No: ${b.registration_number || '-'}\nDate: ${formatDate(b.date_of_booking)}`;
    } else if (selectedReport === 'delivery_note') {
      msg = `*Delivery Note - ${companyDetails?.name || 'AutoBook'}*\n\nB.No: ${b.booking_no}\nCustomer: ${b.customer_first_name} ${b.customer_last_name}\nVehicle: ${b.brand_name} ${b.model}\nChassis: ${b.chassis_no || '-'}\nMotor: ${b.engine_motor_no || '-'}\nReg No: ${b.registration_number || '-'}`;
    } else {
      msg = `*${reportTypes.find(r => r.id === selectedReport)?.label}*\n\nB.No: ${b.booking_no}\nCustomer: ${b.customer_first_name}\nVehicle: ${b.brand_name} ${b.model}`;
    }
    
    { const _a = document.createElement('a'); _a.href = `https://web.whatsapp.com/send?phone=${shareWhatsApp.replace(/[^0-9]/g, '')}&text=${encodeURIComponent(msg)}`; _a.target = '_blank'; _a.rel = 'noopener noreferrer'; document.body.appendChild(_a); _a.click(); document.body.removeChild(_a); }
    setShowShareModal(false);
  };

  const handleEmailShare = () => {
    if (!shareEmail || !selectedBooking) return;
    const b = selectedBooking;
    const subject = `${reportTypes.find(r => r.id === selectedReport)?.label} - B.No ${b.booking_no}`;
    const body = `Booking No: ${b.booking_no}\nCustomer: ${b.customer_first_name} ${b.customer_last_name}\nMobile: ${b.mobile_no_1}\nVehicle: ${b.brand_name} ${b.model}\nChassis: ${b.chassis_no || '-'}\nReg No: ${b.registration_number || '-'}`;
    window.location.href = `mailto:${shareEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setShowShareModal(false);
  };

  const handleDownload = async (format) => {
    if (!selectedBooking) {
      toast.error('Please select a booking first');
      return;
    }
    try {
      toast.info(`Preparing ${format.toUpperCase()} download...`);
      const reportTitle = `${reportTypes.find(r => r.id === selectedReport)?.label}_${selectedBooking.booking_no}`;
      
      const response = await axios.post(`${BACKEND_URL}/api/reports/download`, {
        booking_ids: [selectedBooking.id],
        format: format,
        report_title: reportTitle,
        company_name: companyDetails?.name,
        company_address: [companyDetails?.door_no, companyDetails?.address_1, companyDetails?.city].filter(Boolean).join(', ')
      }, { responseType: 'blob' });
      
      const extensions = { pdf: 'pdf', excel: 'xlsx', word: 'docx' };
      const mimeTypes = { 
        pdf: 'application/pdf', 
        excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 
        word: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      };
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeTypes[format] }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportTitle.replace(/\s+/g, '_')}.${extensions[format]}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded successfully!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to download ${format.toUpperCase()}`);
    }
  };

  const CompanyHeader = () => (
    <div className="flex justify-between items-start mb-4 pb-3 border-b-2 border-gray-400 print:mb-3 print:pb-2">
      <div className="flex items-start gap-3">
        {companyDetails?.logo && (
          <img src={companyDetails.logo} alt="Logo" className="h-12 w-auto print:h-10" />
        )}
        <div className="text-xs print:text-[9pt] leading-tight">
          <h1 className="text-base font-bold print:text-sm">{companyDetails?.name || 'COMPANY NAME'}</h1>
          <p>
            {[companyDetails?.door_no, companyDetails?.building_name, companyDetails?.address_1].filter(Boolean).join(', ')}
          </p>
          <p>
            {[companyDetails?.area, companyDetails?.city, companyDetails?.state].filter(Boolean).join(', ')} - {companyDetails?.pincode || ''}
          </p>
          <p>
            {companyDetails?.landline_1 && `Ph: ${companyDetails.landline_1}`}
            {companyDetails?.mobile && ` | Mob: ${companyDetails.mobile}`}
            {companyDetails?.email && ` | ${companyDetails.email}`}
          </p>
          <p className="font-medium">
            {companyDetails?.gst_no && `GSTIN: ${companyDetails.gst_no}`}
            {companyDetails?.hashtag && ` | ${companyDetails.hashtag}`}
          </p>
        </div>
      </div>
      <div className="text-right text-xs print:text-[9pt]">
        <p className="text-sm font-bold print:text-xs">B.No: {selectedBooking?.booking_no}</p>
        <p>Booking: {formatDate(selectedBooking?.date_of_booking) || '-'}</p>
        <p>Print: {formatDate(new Date().toISOString())}</p>
      </div>
    </div>
  );

  const BookingFormReport = () => (
    <div className="p-6 bg-white font-sans print-container print:p-4" style={{ maxWidth: '210mm' }}>
      <CompanyHeader />
      <h2 className="text-center text-xl font-bold mb-2 underline print:text-lg print:mb-2">BOOKING AND VEHICLE DETAILS</h2>
      
      {/* Customer Balance & Exchange Vehicle - Highlighted Section */}
      <div className="flex justify-center gap-8 mb-4 print:mb-3">
        <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg px-6 py-2 text-center shadow-sm print:px-4 print:py-1">
          <p className="text-xs text-yellow-700 font-medium">CUSTOMER BALANCE</p>
          <p className="text-xl font-bold text-yellow-800 print:text-lg">₹{(selectedBooking?.customer_balance || 0).toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-blue-100 border-2 border-blue-400 rounded-lg px-6 py-2 text-center shadow-sm print:px-4 print:py-1">
          <p className="text-xs text-blue-700 font-medium">EXCHANGE VEHICLE NO</p>
          <p className="text-xl font-bold text-blue-800 print:text-lg">{selectedBooking?.exchange_vehicle_no || 'N/A'}</p>
        </div>
      </div>
      
      {/* Vehicle Details */}
      <div className="mb-4 avoid-break">
        <h3 className="font-bold bg-gray-200 p-2 mb-2 text-base print:text-sm print:p-1">VEHICLE DETAILS</h3>
        <div className="grid grid-cols-2 gap-1 text-base print:text-sm">
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Vehicle Type:</span>{selectedBooking?.brand_name} {selectedBooking?.model}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Variant:</span>{selectedBooking?.variant || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Color:</span>{selectedBooking?.color || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Fuel Type:</span>{selectedBooking?.fuel_type || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Chassis No:</span>{selectedBooking?.chassis_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Motor/Engine No:</span>{selectedBooking?.engine_motor_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Battery No:</span>{selectedBooking?.battery_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Charger No:</span>{selectedBooking?.charger_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Key No:</span>{selectedBooking?.key_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Registration No:</span>{selectedBooking?.registration_number || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">PME Drive No:</span>{selectedBooking?.pme_drive_no || '-'}</div>
        </div>
      </div>

      {/* Customer Details */}
      <div className="mb-4 avoid-break">
        <h3 className="font-bold bg-gray-200 p-2 mb-2 text-base print:text-sm print:p-1">CUSTOMER DETAILS</h3>
        <div className="grid grid-cols-2 gap-1 text-base print:text-sm">
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Customer Name:</span>{selectedBooking?.salutation} {selectedBooking?.customer_first_name} {selectedBooking?.customer_last_name}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Father/Husband:</span>{selectedBooking?.father_husband_name || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">DOB:</span>{formatDate(selectedBooking?.dob) || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Gender:</span>{selectedBooking?.gender || '-'}</div>
          <div className="flex border-b py-1 col-span-2"><span className="w-36 font-medium print:w-32">Address:</span>{selectedBooking?.door_no} {selectedBooking?.building_name} {selectedBooking?.address_1} {selectedBooking?.address_2}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Area:</span>{selectedBooking?.area || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">City:</span>{selectedBooking?.city || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">State:</span>{selectedBooking?.state || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Pincode:</span>{selectedBooking?.pin_code || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Mobile 1:</span>{selectedBooking?.mobile_no_1 || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Mobile 2:</span>{selectedBooking?.mobile_no_2 || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">WhatsApp:</span>{selectedBooking?.whatsapp_no_1 || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Email:</span>{selectedBooking?.email || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Aadhar No:</span>{selectedBooking?.aadhar_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">PAN No:</span>{selectedBooking?.pan_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">GST No:</span>{selectedBooking?.gst_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Financier:</span>{selectedBooking?.financiers_name || 'Cash/Self'}</div>
        </div>
      </div>

      {/* Accessories - Only Selected Ones - WITHOUT Price Column */}
      <div className="mb-4 avoid-break">
        <h3 className="font-bold bg-gray-200 p-2 mb-2 text-base print:text-sm print:p-1">ACCESSORIES</h3>
        {getSelectedAccessories(selectedBooking).length > 0 ? (
          <table className="w-full text-base border print:text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2 w-12 print:p-1 print:w-10">S.No</th>
                <th className="border p-2 text-left print:p-1">Accessory</th>
                <th className="border p-2 w-28 print:p-1 print:w-24">Received</th>
                <th className="border p-2 w-36 print:p-1 print:w-32">Signature</th>
              </tr>
            </thead>
            <tbody>
              {getSelectedAccessories(selectedBooking).map((acc, idx) => (
                <tr key={acc.name}>
                  <td className="border p-2 text-center print:p-1">{idx + 1}</td>
                  <td className="border p-2 print:p-1">{acc.name}</td>
                  <td className="border p-2 text-center print:p-1">☐ Yes ☐ No</td>
                  <td className="border p-2 print:p-1 h-10"></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-base text-gray-500 p-2 border print:text-sm">No accessories selected for this booking</p>
        )}
      </div>

      {/* Standard Items Checklist */}
      <div className="mb-4 avoid-break">
        <h3 className="font-bold bg-gray-200 p-2 mb-2 text-base print:text-sm print:p-1">STANDARD ITEMS</h3>
        <table className="w-full text-base border print:text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border p-2 text-left print:p-1">Description</th>
              <th className="border p-2 w-24 print:p-1 print:w-20">Received</th>
              <th className="border p-2 w-28 print:p-1 print:w-24">Signature</th>
            </tr>
          </thead>
          <tbody>
            {['Service Manual', 'Tools Kit', 'First Aid Kit', 'Helmet', 'Insurance Copy', 'Invoice Copy', 'Mobile App Download'].map((item, i) => (
              <tr key={i}>
                <td className="border p-2 print:p-1">{item}</td>
                <td className="border p-2 text-center print:p-1">☐ Yes ☐ No</td>
                <td className="border p-2 print:p-1 h-8"></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Signatures */}
      <div className="flex justify-between mt-12 pt-6 signature-area print:mt-8 print:pt-4">
        <div className="text-center">
          <div className="border-t-2 border-black w-44 pt-2 print:w-36">Customer Signature</div>
        </div>
        <div className="text-center">
          <p className="font-medium mb-6 print:mb-4">For {companyDetails?.name || 'Company'}</p>
          <div className="border-t-2 border-black w-44 pt-2 print:w-36">Authorized Signatory</div>
        </div>
      </div>
    </div>
  );

  const DeliveryNoteReport = () => (
    <div className="p-4 bg-white font-sans print-container print:p-3" style={{ maxWidth: '210mm' }}>
      <CompanyHeader />
      <h2 className="text-center text-base font-bold mb-2 underline print:text-sm print:mb-1">DELIVERY NOTE</h2>
      
      <p className="mb-2 text-xs leading-tight print:text-[9pt] print:mb-1">
        I/We hereby acknowledge that the Dealer <strong>{companyDetails?.name || 'Company'}</strong> located at {companyDetails?.address_1 || ''} {companyDetails?.city || ''} has delivered the following described vehicle to me/us at the above said address:
      </p>

      {/* Vehicle Details */}
      <div className="mb-2 avoid-break">
        <h3 className="font-bold bg-gray-200 p-1 mb-1 text-xs print:text-[9pt] print:p-0.5">VEHICLE DETAILS</h3>
        <div className="grid grid-cols-2 gap-0.5 text-xs print:text-[9pt]">
          <div className="flex border-b py-0.5"><span className="w-28 font-medium print:w-24">Vehicle Type:</span>{selectedBooking?.brand_name} {selectedBooking?.model}</div>
          <div className="flex border-b py-0.5"><span className="w-28 font-medium print:w-24">Variant:</span>{selectedBooking?.variant || '-'}</div>
          <div className="flex border-b py-0.5"><span className="w-28 font-medium print:w-24">Color:</span>{selectedBooking?.color || '-'}</div>
          <div className="flex border-b py-0.5"><span className="w-28 font-medium print:w-24">Chassis No:</span>{selectedBooking?.chassis_no || '-'}</div>
          <div className="flex border-b py-0.5"><span className="w-28 font-medium print:w-24">Motor/Engine No:</span>{selectedBooking?.engine_motor_no || '-'}</div>
          <div className="flex border-b py-0.5"><span className="w-28 font-medium print:w-24">Battery No:</span>{selectedBooking?.battery_no || '-'}</div>
          <div className="flex border-b py-0.5"><span className="w-28 font-medium print:w-24">Charger No:</span>{selectedBooking?.charger_no || '-'}</div>
          <div className="flex border-b py-0.5"><span className="w-28 font-medium print:w-24">Registration No:</span>{selectedBooking?.registration_number || '-'}</div>
        </div>
      </div>

      {/* Customer Details */}
      <div className="mb-2 avoid-break">
        <h3 className="font-bold bg-gray-200 p-1 mb-1 text-xs print:text-[9pt] print:p-0.5">CUSTOMER DETAILS</h3>
        <div className="grid grid-cols-2 gap-0.5 text-xs print:text-[9pt]">
          <div className="flex border-b py-0.5"><span className="w-28 font-medium print:w-24">Customer Name:</span>{selectedBooking?.customer_first_name} {selectedBooking?.customer_last_name}</div>
          <div className="flex border-b py-0.5"><span className="w-28 font-medium print:w-24">Father/Husband:</span>{selectedBooking?.father_husband_name || '-'}</div>
          <div className="flex border-b py-0.5 col-span-2"><span className="w-28 font-medium print:w-24">Address:</span>{selectedBooking?.address_1} {selectedBooking?.address_2}</div>
          <div className="flex border-b py-0.5"><span className="w-28 font-medium print:w-24">Area:</span>{selectedBooking?.area || '-'}</div>
          <div className="flex border-b py-0.5"><span className="w-28 font-medium print:w-24">Pincode:</span>{selectedBooking?.pin_code || '-'}</div>
          <div className="flex border-b py-0.5"><span className="w-28 font-medium print:w-24">Phone:</span>{selectedBooking?.mobile_no_1 || '-'}</div>
          <div className="flex border-b py-0.5"><span className="w-28 font-medium print:w-24">Financier:</span>{selectedBooking?.financiers_name || 'Cash/Self'}</div>
        </div>
      </div>

      {/* Accessories - Only Selected Ones */}
      {getSelectedAccessories(selectedBooking).length > 0 && (
        <div className="mb-2 avoid-break">
          <h3 className="font-bold bg-gray-100 p-1 mb-1 text-xs print:text-[9pt] print:p-0.5">ACCESSORIES</h3>
          <table className="w-full text-xs border print:text-[8pt]">
            <thead>
              <tr className="bg-gray-50">
                <th className="border p-1 w-8 print:p-0.5">S.No</th>
                <th className="border p-1 text-left print:p-0.5">Accessory</th>
                <th className="border p-1 w-16 print:p-0.5">Received</th>
                <th className="border p-1 w-24 print:p-0.5">Signature</th>
              </tr>
            </thead>
            <tbody>
              {getSelectedAccessories(selectedBooking).map((acc, idx) => (
                <tr key={acc.name}>
                  <td className="border p-1 text-center print:p-0.5">{idx + 1}</td>
                  <td className="border p-1 print:p-0.5">{acc.name}</td>
                  <td className="border p-1 text-center print:p-0.5">☐ Yes ☐ No</td>
                  <td className="border p-1 print:p-0.5 h-6"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Items Checklist */}
      <div className="mb-2 text-xs border p-2 print:text-[9pt] print:p-1">
        <p className="font-bold mb-1">Standard Items Received:</p>
        <div className="grid grid-cols-2 gap-1">
          <div>1. Service Manual: ☐ Yes ☐ No</div>
          <div>2. Tool Kit: ☐ Yes ☐ No</div>
          <div>3. Battery & Accessories: ☐ Yes ☐ No</div>
          <div>4. Warranty Policy: ☐ Yes ☐ No</div>
        </div>
      </div>

      <p className="mb-2 text-xs font-medium print:text-[9pt] print:mb-1">
        I hereby acknowledge that I have received the vehicle in good condition to my/our satisfaction.
      </p>

      {/* Important Notes */}
      <div className="mb-2 border p-2 bg-gray-50 print:p-1">
        <h3 className="font-bold mb-1 text-xs print:text-[8pt]">IMPORTANT NOTES:</h3>
        <ul className="text-[10px] list-disc pl-4 space-y-0.5 print:text-[8pt]">
          <li>Charge battery for 6-8 hours before first use. Do not overcharge beyond 8 hours.</li>
          <li>Range depends on road conditions, rider weight, and speed.</li>
          <li>Service vehicle as per scheduled intervals. Contact dealer for support.</li>
        </ul>
      </div>

      {/* Signatures */}
      <div className="flex justify-between mt-4 pt-2 print:mt-3 print:pt-1">
        <div>
          <p className="font-medium text-xs print:text-[9pt]">For {companyDetails?.name || 'Company'}</p>
          <div className="mt-6 border-t-2 border-black w-36 pt-1 text-xs print:mt-4 print:w-32">Authorized Signatory</div>
          <p className="text-[10px] mt-1 print:text-[8pt]">Date: _______________</p>
          <p className="text-[10px] print:text-[8pt]">Delivery Done by: _______________</p>
        </div>
        <div className="text-center">
          <div className="mt-6 border-t-2 border-black w-36 pt-1 text-xs print:mt-4 print:w-32">Customer Signature</div>
        </div>
      </div>
    </div>
  );

  const PDIReport = () => (
    <div className="p-6 bg-white font-sans print-container print:p-4" style={{ maxWidth: '210mm' }}>
      <CompanyHeader />
      <h2 className="text-center text-xl font-bold mb-2 underline print:text-lg">PDI REPORT</h2>
      <p className="text-center text-base mb-4 print:text-sm">(Pre-Delivery Inspection)</p>

      {/* Customer Details */}
      <div className="mb-4 avoid-break">
        <h3 className="font-bold bg-gray-200 p-2 mb-2 text-base print:text-sm print:p-1">CUSTOMER DETAILS</h3>
        <div className="grid grid-cols-2 gap-1 text-base print:text-sm">
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Customer Name:</span>{selectedBooking?.salutation} {selectedBooking?.customer_first_name} {selectedBooking?.customer_last_name}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Father/Husband:</span>{selectedBooking?.father_husband_name || '-'}</div>
          <div className="flex border-b py-1 col-span-2"><span className="w-36 font-medium print:w-32">Address:</span>{selectedBooking?.door_no} {selectedBooking?.building_name} {selectedBooking?.address_1} {selectedBooking?.address_2}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Area:</span>{selectedBooking?.area || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">City:</span>{selectedBooking?.city || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">State:</span>{selectedBooking?.state || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Pincode:</span>{selectedBooking?.pin_code || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Mobile:</span>{selectedBooking?.mobile_no_1 || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">WhatsApp:</span>{selectedBooking?.whatsapp_no_1 || '-'}</div>
        </div>
      </div>

      {/* Vehicle Details */}
      <div className="mb-4 avoid-break">
        <h3 className="font-bold bg-gray-200 p-2 mb-2 text-base print:text-sm print:p-1">VEHICLE DETAILS</h3>
        <div className="grid grid-cols-2 gap-1 text-base print:text-sm">
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Vehicle Type:</span>{selectedBooking?.brand_name} {selectedBooking?.model}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Variant:</span>{selectedBooking?.variant || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Color:</span>{selectedBooking?.color || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Fuel Type:</span>{selectedBooking?.fuel_type || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Chassis No:</span>{selectedBooking?.chassis_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Motor/Engine No:</span>{selectedBooking?.engine_motor_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Battery No:</span>{selectedBooking?.battery_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Charger No:</span>{selectedBooking?.charger_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Key No:</span>{selectedBooking?.key_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">Registration No:</span>{selectedBooking?.registration_number || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium print:w-32">PME Drive No:</span>{selectedBooking?.pme_drive_no || '-'}</div>
        </div>
      </div>

      {/* PDI Check Points */}
      <div className="mb-4 avoid-break">
        <h3 className="font-bold bg-gray-200 p-2 mb-2 text-base print:text-sm print:p-1">PDI CHECK POINTS</h3>
        <table className="w-full text-base border print:text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border p-2 w-12 print:p-1 print:w-10">S.No</th>
              <th className="border p-2 text-left print:p-1">Job Description</th>
              <th className="border p-2 w-28 print:p-1 print:w-24">Status</th>
            </tr>
          </thead>
          <tbody>
            {pdiCheckPoints.map((job, i) => (
              <tr key={i}>
                <td className="border p-2 text-center print:p-1">{i + 1}</td>
                <td className="border p-2 print:p-1">{job}</td>
                <td className="border p-2 text-center print:p-1">☐ OK ☐ Not OK</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Accessories - Only Selected Ones - WITHOUT Price Column */}
      <div className="mb-4 avoid-break">
        <h3 className="font-bold bg-gray-200 p-2 mb-2 text-base print:text-sm print:p-1">ACCESSORIES</h3>
        {getSelectedAccessories(selectedBooking).length > 0 ? (
          <table className="w-full text-base border print:text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2 w-12 print:p-1 print:w-10">S.No</th>
                <th className="border p-2 text-left print:p-1">Accessory</th>
                <th className="border p-2 w-28 print:p-1 print:w-24">Received</th>
                <th className="border p-2 w-36 print:p-1 print:w-32">Signature</th>
              </tr>
            </thead>
            <tbody>
              {getSelectedAccessories(selectedBooking).map((acc, idx) => (
                <tr key={acc.name}>
                  <td className="border p-2 text-center print:p-1">{idx + 1}</td>
                  <td className="border p-2 print:p-1">{acc.name}</td>
                  <td className="border p-2 text-center print:p-1">☐ Yes ☐ No</td>
                  <td className="border p-2 print:p-1 h-10"></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-base text-gray-500 p-2 border print:text-sm">No accessories selected for this booking</p>
        )}
      </div>

      {/* Signatures */}
      <div className="flex justify-between mt-8 pt-6 signature-area print:mt-6 print:pt-4">
        <div className="text-center">
          <div className="border-t-2 border-black w-44 pt-2 print:w-36">Inspected By</div>
          <p className="text-sm mt-2 print:text-xs">Date: _______________</p>
        </div>
        <div className="text-center">
          <div className="border-t-2 border-black w-44 pt-2 print:w-36">Approved By</div>
        </div>
      </div>
    </div>
  );

  const WarrantyLetterReport = () => (
    <div className="p-8 bg-white font-sans" style={{ width: '210mm', minHeight: '297mm' }}>
      <CompanyHeader />
      <h2 className="text-center text-xl font-bold mb-6 underline">WARRANTY ACKNOWLEDGEMENT LETTER</h2>

      <div className="mb-6 text-sm border-l-4 border-gray-400 pl-4">
        <p className="font-medium">From:</p>
        <p>{selectedBooking?.salutation} {selectedBooking?.customer_first_name} {selectedBooking?.customer_last_name}</p>
        <p>{selectedBooking?.address_1} {selectedBooking?.address_2}</p>
        <p>{selectedBooking?.area} - {selectedBooking?.pin_code}</p>
        <p>Mobile: {selectedBooking?.mobile_no_1}</p>
      </div>

      <p className="mb-4 text-sm">Dear Sir,</p>

      <p className="mb-4 text-sm leading-relaxed">
        I/We hereby acknowledge that the Dealer <strong>{companyDetails?.name || 'Company'}</strong> has thoroughly explained the Warranty terms and conditions* with respect to Battery, Motor, Controller, Charger and other spare parts.
      </p>

      <div className="mb-6 text-sm bg-gray-100 p-4 border-l-4 border-blue-500">
        <p className="font-bold mb-2">*Warranty terms and conditions:</p>
        <p className="leading-relaxed mb-2">
          The warranty with respect to any parts can be availed only when all the scheduled service is done on the stipulated date of service or Kms. If even one service is lapsed or not availed, then warranty of the vehicle becomes void.
        </p>
        <p className="leading-relaxed">
          I hereby acknowledge that if I/We have not availed the service as per the scheduled service, I/We will not hold the dealer/manufacturer for the same.
        </p>
      </div>

      {/* Vehicle Details */}
      <div className="mb-6">
        <h3 className="font-bold bg-gray-200 p-2 mb-2 text-sm">VEHICLE DETAILS</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex border-b py-1"><span className="w-36 font-medium">B.No:</span>{selectedBooking?.booking_no}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium">Date:</span>{formatDate(selectedBooking?.date_of_booking) || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium">Vehicle Type:</span>{selectedBooking?.brand_name} {selectedBooking?.model}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium">Variant:</span>{selectedBooking?.variant || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium">Color:</span>{selectedBooking?.color || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium">Chassis No:</span>{selectedBooking?.chassis_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium">Motor/Engine No:</span>{selectedBooking?.engine_motor_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium">Battery No:</span>{selectedBooking?.battery_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium">Charger No:</span>{selectedBooking?.charger_no || '-'}</div>
          <div className="flex border-b py-1"><span className="w-36 font-medium">Registration No:</span>{selectedBooking?.registration_number || '-'}</div>
        </div>
      </div>

      <div className="mb-8 text-sm">
        <p>Thanking you,</p>
        <p>Yours faithfully,</p>
      </div>

      {/* Signatures */}
      <div className="flex justify-between mt-16 pt-8">
        <div className="text-center">
          <div className="border-t-2 border-black w-48 pt-2">Customer Signature</div>
          <p className="text-sm mt-2">Date: _______________</p>
        </div>
        <div>
          <p className="font-medium">For {companyDetails?.name || 'Company'}</p>
          <div className="mt-12 border-t-2 border-black w-48 pt-2">Authorized Signatory</div>
        </div>
      </div>
    </div>
  );

  const renderReport = () => {
    if (!selectedBooking) return null;
    switch (selectedReport) {
      case 'booking_form': return <BookingFormReport />;
      case 'delivery_note': return <DeliveryNoteReport />;
      case 'pdi_report': return <PDIReport />;
      case 'warranty_letter': return <WarrantyLetterReport />;
      default: return null;
    }
  };

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Reports">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left Panel - Booking Selection */}
        <div className="bg-white p-4 rounded-lg border">
          <h3 className="font-bold mb-4">Select Booking</h3>
          <input
            type="text"
            placeholder="Search by B.No, Name, or Chassis..."
            className="w-full p-2 border rounded mb-2 text-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {/* Top Pagination */}
          <div className="mb-2 flex justify-end">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredBookings.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              showInfo={true}
            />
          </div>
          <div className="max-h-80 overflow-y-auto border rounded">
            {paginatedBookings.map(b => (
              <div
                key={b.id}
                onClick={() => setSelectedBooking(b)}
                className={`p-2 border-b cursor-pointer hover:bg-gray-50 ${selectedBooking?.id === b.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
              >
                <div className="font-medium text-sm">{b.booking_no}</div>
                <div className="text-xs text-gray-600">{b.customer_first_name} {b.customer_last_name}</div>
                <div className="text-xs text-gray-500">{b.chassis_no}</div>
              </div>
            ))}
            {paginatedBookings.length === 0 && (
              <div className="p-4 text-center text-gray-500 text-sm">No bookings found</div>
            )}
          </div>
        </div>

        {/* Report Type Selection */}
        <div className="bg-white p-4 rounded-lg border">
          <h3 className="font-bold mb-4">Report Type</h3>
          <div className="space-y-2">
            {reportTypes.map(type => (
              <div
                key={type.id}
                onClick={() => setSelectedReport(type.id)}
                className={`p-3 border rounded cursor-pointer hover:bg-gray-50 ${selectedReport === type.id ? 'bg-blue-50 border-blue-500' : ''}`}
              >
                <div className="font-medium text-sm">{type.label}</div>
                <div className="text-xs text-gray-600">{type.description}</div>
              </div>
            ))}
          </div>

          {selectedBooking && (
            <div className="mt-4 space-y-2">
              <button
                onClick={handlePrint}
                data-testid="reports-print-btn"
                className="w-full px-4 py-3 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                🖨️ Print Report
              </button>
              <div className="relative group">
                <button
                  data-testid="reports-download-btn"
                  className="w-full px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 flex items-center justify-center gap-2"
                >
                  ⬇️ Download ▼
                </button>
                <div className="absolute left-0 right-0 mt-1 bg-white border rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
                  <button onClick={() => handleDownload('pdf')} data-testid="reports-download-pdf" className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm">📄 Download PDF</button>
                  <button onClick={() => handleDownload('excel')} data-testid="reports-download-excel" className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm">📊 Download Excel</button>
                  <button onClick={() => handleDownload('word')} data-testid="reports-download-word" className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm">📝 Download Word</button>
                </div>
              </div>
              <button
                onClick={() => setShowShareModal(true)}
                data-testid="reports-share-btn"
                className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center justify-center gap-2"
              >
                📤 Share (Email/WhatsApp)
              </button>
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="lg:col-span-2 bg-white p-4 rounded-lg border">
          <h3 className="font-bold mb-4">Preview</h3>
          {selectedBooking ? (
            <div className="border rounded overflow-auto max-h-[600px]" style={{ transform: 'scale(0.45)', transformOrigin: 'top left', width: '222%', height: '222%' }}>
              <div ref={printRef}>
                {renderReport()}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12">
              Select a booking to preview report
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Share Report</h3>
            
            <div className="space-y-4">
              {/* Attachment Format Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Attachment Format</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShareFormat('pdf')}
                    data-testid="reports-format-pdf"
                    className={`flex-1 p-3 rounded border-2 transition-colors ${
                      shareFormat === 'pdf' 
                        ? 'border-blue-600 bg-blue-50 text-blue-700' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-lg mb-1">📄</div>
                    <div className="text-sm font-medium">PDF</div>
                  </button>
                  <button
                    onClick={() => setShareFormat('excel')}
                    data-testid="reports-format-excel"
                    className={`flex-1 p-3 rounded border-2 transition-colors ${
                      shareFormat === 'excel' 
                        ? 'border-green-600 bg-green-50 text-green-700' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-lg mb-1">📊</div>
                    <div className="text-sm font-medium">Excel</div>
                  </button>
                  <button
                    onClick={() => setShareFormat('word')}
                    data-testid="reports-format-word"
                    className={`flex-1 p-3 rounded border-2 transition-colors ${
                      shareFormat === 'word' 
                        ? 'border-purple-600 bg-purple-50 text-purple-700' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-lg mb-1">📝</div>
                    <div className="text-sm font-medium">Word</div>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {shareFormat === 'pdf' && 'PDF - Best for printing and sharing formal reports'}
                  {shareFormat === 'excel' && 'Excel - Best for data analysis and manipulation'}
                  {shareFormat === 'word' && 'Word - Best for editing and customization'}
                </p>
              </div>

              <hr className="my-4" />

              <div>
                <label className="block text-sm font-medium mb-1">Email Address</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    className="flex-1 p-2 border rounded"
                    placeholder="email@example.com"
                    data-testid="reports-share-email-input"
                  />
                  <button onClick={handleEmailShare} data-testid="reports-share-email-btn" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                    📧 Send
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">WhatsApp Number</label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={shareWhatsApp}
                    onChange={(e) => setShareWhatsApp(e.target.value)}
                    className="flex-1 p-2 border rounded"
                    placeholder="919876543210"
                    data-testid="reports-share-whatsapp-input"
                  />
                  <button onClick={handleWhatsAppShare} data-testid="reports-share-whatsapp-btn" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                    💬 Send
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Include country code (e.g., 91 for India)</p>
              </div>
            </div>

            <button
              onClick={() => setShowShareModal(false)}
              data-testid="reports-share-modal-close"
              className="w-full mt-4 px-4 py-2 border rounded hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </SalesLayout>
  );
}

export default Reports;
